/*
 * Created Date: 22-01-2024, 8:58:54 am
 * Author: Victor
 * Email: victor@pt-corp.vn
 * -----
 * Last Modified: Wed Jan 24 2024
 * Modified By: Victor
 * -----
 * Copyright (c) 2024 PT CORP, Inc
 * -----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	----------------------------------------------------------
 */

import { Chip, FormHelperText, FormLabel, InputLabel, Typography } from '@mui/material'
import { Box } from '@mui/system'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormAdd } from './CoreAutocomplete'

const CoreSelectChip = props => {
	const { control, label, name, containerClassName, type, required, chipColor, options, showAdd, addDataKey } = props

	const { t } = useTranslation()
	const {
		field: { value, onBlur, onChange, ref },
		fieldState: { error }
	} = useController({
		name,
		control
	})

	const [customAddData, setCustomAddedData] = useState([])

	const filteredOptions = useMemo(() => {
		return options.concat(customAddData)
	}, [options, JSON.stringify(customAddData)])

	const handleValueChange = itemValue => {
		if (type === 'single') {
			return onChange(itemValue)
		}
		if (type === 'multiple') {
			const isExisted = value?.some(i => i === itemValue)

			onChange(isExisted ? value?.filter(item => item !== itemValue) : [...value, itemValue], {
				shouldDirty: true,
				shouldValidate: true
			})
		}
	}

	const isChecked = itemValue => {
		if (type === 'single' && value === itemValue) {
			return true
		}
		if (type === 'multiple' && value?.includes(itemValue)) {
			return true
		}
		return false
	}

	return (
		<Box className={containerClassName ?? 'flex  flex-col'}>
			{label ? (
				<FormLabel className="mb-2 font-medium text-label" required={required} htmlFor={name}>
					{label}
				</FormLabel>
			) : null}

			<div className="flex flex-wrap items-center w-full gap-2">
				{showAdd ? (
					<FormAdd
						addDataKey={addDataKey ?? 'name'}
						setValue={onChange}
						value={value}
						addMode="static"
						setCustomAddedData={setCustomAddedData}
					/>
				) : null}
				{filteredOptions.map(item => (
					<Chip
						className="min-w-[60px]"
						key={item.value}
						label={item.label}
						color={isChecked(item?.value) ? chipColor || 'primary' : 'default'}
						onClick={() => handleValueChange(item.value)}
						disabled={item?.disabled ?? false}
					/>
				))}
			</div>
			{error?.message ? <FormHelperText error>{error.message}</FormHelperText> : null}
			{filteredOptions?.length === 0 && !showAdd && (
				<Typography variant="helper" className="mt-[0.2rem]">
					{t('Không có lựa chọn nào')}
				</Typography>
			)}
		</Box>
	)
}

CoreSelectChip.defaultProps = {
	type: 'single',
	options: [],
	showAdd: false
}

CoreSelectChip.propTypes = {
	type: PropTypes.oneOf(['single', 'multiple']).isRequired,
	control: PropTypes.object.isRequired,
	name: PropTypes.string.isRequired,
	label: PropTypes.element,
	placeholder: PropTypes.element,
	containerClassName: PropTypes.string,
	chipColor: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.element.isRequired,
			value: PropTypes.any.isRequired
		})
	).isRequired,
	showAdd: PropTypes.bool,
	addMode: PropTypes.oneOf(['static', 'api']),
	addDataKey: PropTypes.string,
	required: PropTypes.bool
}

export default React.memo(CoreSelectChip)
